import { useState } from "react";
import { SvgIcon } from "@progress/kendo-react-common";
import {
  checkCircleIcon,
  xCircleIcon,
  clockIcon,
} from "@progress/kendo-svg-icons";
import { ICON_BASE_PATH } from "@/app/utils/config";
import { AnimatePresence, motion } from "framer-motion";
import { gxDateTimeFormat } from "@/app/utils/helpers/gxDateTimeFormat";
import { ChevronDown, ChevronUp } from "lucide-react";
import type { PaymentMetromap } from "@/app/types/bulkPaymentsPayroll";
import {
  getBulkMetroMapResolved,
  type MetroStepResolved,
  type MetroSubItem,
} from "@/app/utils/BulkPaymentPayroll/bulkMetroMap.helper";
import type { MetroStepState } from "@/app/utils/BulkPaymentPayroll/bulkMetroMap.config";
import "./styles/BulkPaymentReview.scss";

// ============================================================================
// PROPS
// ============================================================================

export interface BulkUploadsMetroMapProps {
  status: string | number;
  jobStatusDesc?: string;
  bulkType?: string;
  paymentMetromap?: PaymentMetromap | null;
  payrollMetromap?: PaymentMetromap | null;
}

// ============================================================================
// VISUAL HELPERS
// ============================================================================

const stepSvgIcon = (state: MetroStepState) => {
  switch (state) {
    case "completed":
      return checkCircleIcon;
    case "failed":
      return xCircleIcon;
    default:
      return clockIcon;
  }
};

const stepIconColor = (state: MetroStepState): string => {
  switch (state) {
    case "completed":
      return "#16a34a";
    case "failed":
      return "#ef4444";
    case "active":
      return "#fb923c";
    default:
      return "#d1d5db";
  }
};

const stepImgIcon = (state: MetroStepState): string => {
  switch (state) {
    case "completed":
      return `${ICON_BASE_PATH}green_tick.svg`;
    case "failed":
      return `${ICON_BASE_PATH}failed-icon.svg`;
    default:
      return `${ICON_BASE_PATH}waiting.svg`;
  }
};

const stepLineClass = (state: MetroStepState): string => {
  switch (state) {
    case "completed":
      return "payment-progress__line payment-progress__line--ok";
    case "failed":
      return "payment-progress__line payment-progress__line--error";
    default:
      return "payment-progress__line payment-progress__line--warn";
  }
};

// ============================================================================
// SUB-ITEM ROW
// ============================================================================

const SubItemRow: React.FC<{ item: MetroSubItem; index: number }> = ({
  item,
  index,
}) => {
  const displayName = item.name?.trim() || `Approver ${index + 1}`;

  return (
    <li className="gx-approval-list-item gx-p12-m">
      <img src={stepImgIcon(item.state)} alt={item.state} className="gx-icon" />
      <span className="gx-approver-name gx-p12-m">{displayName}</span>
      {item.state !== "pending" && item.date && (
        <span className="gx-approval-date gx-p10-r">
          {gxDateTimeFormat(item.date)}
        </span>
      )}
      {item.state === "failed" && item.remarks && (
        <span
          className="gx-p10-r"
          style={{ color: "#6b7280", fontStyle: "italic" }}
        >
          Remarks: {item.remarks}
        </span>
      )}
    </li>
  );
};

// ============================================================================
// APPROVAL SUMMARY HEADER  (e.g. "1 of 2 Approvals Completed")
// ============================================================================

const ApprovalSummaryHeader: React.FC<{
  subItems: MetroSubItem[];
  approvalsRequired?: number;
}> = ({ subItems, approvalsRequired }) => {
  const completed = subItems.filter((i) => i.state === "completed").length;
  const total = approvalsRequired ?? subItems.length;
  if (total === 0) return null;

  return (
    <p className="gx-p12-m" style={{ marginBottom: 8, color: "#374151" }}>
      {completed} of {total} Approval{total !== 1 ? "s" : ""} Completed
    </p>
  );
};

// ============================================================================
// SINGLE TIMELINE STEP
// ============================================================================

interface StepProps {
  step: MetroStepResolved;
  isExpanded: boolean;
  onToggle: () => void;
  showConnector: boolean;
  isInitiated: boolean;
  approvalsRequired?: number;
}

const MetroStep: React.FC<StepProps> = ({
  step,
  isExpanded,
  onToggle,
  showConnector,
  isInitiated,
  approvalsRequired,
}) => {
  const hasExpandableContent = isInitiated
    ? !!step.subItems?.[0]
    : !!(step.subItems && step.subItems.length > 0);

  return (
    <li className="payment-progress__step">
      {/* Icon + vertical connector */}
      <div className="payment-progress__icon-col">
        <SvgIcon
          icon={stepSvgIcon(step.state)}
          style={{ color: stepIconColor(step.state) }}
          className="payment-progress__icon"
        />
        {showConnector && <span className={stepLineClass(step.state)} />}
      </div>

      {/* Content */}
      <div className="gx-step-connect">
        <button type="button" className="gx-step-pointer" onClick={onToggle}>
          <h4 className="gx-ape-completed">
            <span className="gx-p14-md">{step.label}</span>
          </h4>
          {step.date && (
            <span className="gx-bulk-metro-date gx-p10">
              {gxDateTimeFormat(step.date)}
            </span>
          )}
        </button>

        <AnimatePresence>
          {isExpanded && hasExpandableContent && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="gx-approval-card"
            >
              {/* Initiated step: show "Initiated by" card */}
              {isInitiated && step.subItems?.[0] && (
                <div className="gx-approval-created">
                  <p className="gx-p10 payment-progress__info-label">
                    Initiated by:
                  </p>
                  <p className="gx-p12-m">{step.subItems[0].name || "—"}</p>
                </div>
              )}

              {/* Approval step: show approvals summary + approver list */}
              {!isInitiated && step.subItems && step.subItems.length > 0 && (
                <div className="gx-approval-created gx-p12-m payment-progress__approval-list">
                  <ApprovalSummaryHeader
                    subItems={step.subItems}
                    approvalsRequired={approvalsRequired}
                  />
                  <ul className="gx-approvals-list">
                    {step.subItems.map((item, idx) => (
                      <SubItemRow
                        key={`${item.name}-${idx}`}
                        item={item}
                        index={idx}
                      />
                    ))}
                  </ul>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </li>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const BulkUploadsMetroMap: React.FC<BulkUploadsMetroMapProps> = ({
  status,
  jobStatusDesc,
  bulkType = "PAYMENT",
  paymentMetromap,
  payrollMetromap,
}) => {
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(
    new Set(["initiated", "approval", "processed"]),
  );
  const [isBulkDetailsOpen, setIsBulkDetailsOpen] = useState(true);

  const resolved = getBulkMetroMapResolved({
    jobStatusCode: status,
    jobStatusDesc,
    paymentMetromap,
    payrollMetromap,
    bulkType,
  });

  if (!resolved) return null;

  const { steps, approvalsRequired } = resolved;

  const toggleStep = (key: string) => {
    setExpandedSteps((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  return (
    <div className="payment-progress-td gx-bulk-metro-map">
      <button
        type="button"
        className={`gx-bpr-bfd-heading ${isBulkDetailsOpen ? "gx-bpr-bfd-heading--active" : ""}`}
        onClick={() => setIsBulkDetailsOpen((prev) => !prev)}
      >
        <h3 className="gx-p18-b">Payment Timeline</h3>
        {isBulkDetailsOpen ? (
          <ChevronUp size={20} />
        ) : (
          <ChevronDown size={20} />
        )}
      </button>

      {isBulkDetailsOpen && (
        <ul className="gx-bulk-metro-timeline">
          {steps.map((step, idx) => (
            <MetroStep
              key={step.key}
              step={step}
              isExpanded={expandedSteps.has(step.key)}
              onToggle={() => toggleStep(step.key)}
              showConnector={idx < steps.length - 1}
              isInitiated={step.key === "initiated"}
              approvalsRequired={
                step.key === "approval" ? approvalsRequired : undefined
              }
            />
          ))}
        </ul>
      )}
    </div>
  );
};

export default BulkUploadsMetroMap;
